import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ClientService, InvoiceService, ClaimService, ToastMessageService, PaymentService, SalesReturnService, RefundService } from '@fitness-force/api';
import { AppErrors } from '@fitness-force/models';;
import { PdfData, PdfType } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { StyleClassModule } from 'primeng/styleclass';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'popup-element',
  templateUrl: './common-pdf-popup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    StyleClassModule,
    DialogModule,
    PopoverModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    PdfViewerModule,
    ProgressSpinnerModule,
    TranslateModule, AsyncPipe]
})
export class CommonPdfPopupComponent implements OnInit {
  private store = inject(Store);
  private translate = inject(TranslateService);
  private clientService = inject(ClientService);
  private invoiceService = inject(InvoiceService);
  private fb = inject(UntypedFormBuilder);
  private http = inject(HttpClient);
  private claimService = inject(ClaimService);
  private toastMessageService = inject(ToastMessageService);
  private cd = inject(ChangeDetectorRef);
  private paymentService = inject(PaymentService);
  private salesReturnService = inject(SalesReturnService);
  private ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  refundService = inject(RefundService);


  pdfSrc = new BehaviorSubject<string>('');
  pdfData : PdfData;
  btnLoading=false;
  voidForm: UntypedFormGroup = new UntypedFormGroup({});
  showVoid:boolean=false;
  showSpinner = new BehaviorSubject<boolean>(false);
  dataUpdated:boolean=false;

  ngOnInit(): void {
    if(this.config.data) this.pdfData= this.config.data as PdfData;
    if (this.pdfData.primaryId > 0)
      this.checkType(this.pdfData.type);
  }

  ngAfterViewInit() {
    this.voidForm = this.fb.group({
      void_reason: ['', [Validators.required]],
    });
  }

  checkType(type: PdfType) {
    switch (type) {
      case PdfType.Invoice:
        {
          this.GetInvoicePdf();
        }
        break;
      case PdfType.Payment:
        {
          this.GetPaymentPdf();
        }
        break;
      case PdfType.SalesReturn:
        {
          this.GetSalesReturnPdf();
        }
        break;
      case PdfType.Refund:
        {
          this.GetRefundPdf();
        }
        break;
    }
    this.cd.detectChanges();
  }

  GetInvoicePdf() {
    this.showSpinner.next(true);
    this.invoiceService.getInvoicePDF(this.pdfData.clientId, this.pdfData.primaryId)
      .subscribe({
        next: (data: any) => {
          this.pdfSrc.next(data.url);
          this.showSpinner.next(false);
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.showSpinner.next(false);
          this.ShowToastErrors(error);
          this.cd.detectChanges();
        },
      });
  }

  GetPaymentPdf() {
    this.showSpinner.next(true);
    this.paymentService.getPaymentPDF(this.pdfData.clientId, this.pdfData.primaryId)
      .subscribe({
        next: (data: any) => {
          this.pdfSrc.next(data.url);
          this.showSpinner.next(false);
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.ShowToastErrors(error);
          this.showSpinner.next(false);
          this.cd.detectChanges();
        },
      });
  }

  GetSalesReturnPdf() {
    this.showSpinner.next(true);
    this.salesReturnService.GetSalesReturnPrint(this.pdfData.clientId,this.pdfData.secondaryId, this.pdfData.primaryId)
      .subscribe({
        next: (data: any) => {
          this.pdfSrc.next(data.url);
          this.showSpinner.next(false);
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.ShowToastErrors(error);
          this.showSpinner.next(false);
          this.cd.detectChanges();
        },
      });
  }

  GetRefundPdf() {
    this.showSpinner.next(true);
    this.refundService.GetRefundPrint(this.pdfData.clientId,this.pdfData.primaryId)
      .subscribe({
        next: (data: any) => {
          this.pdfSrc.next(data.url);
          this.showSpinner.next(false);
          this.cd.detectChanges();
        },
        error: (error: AppErrors) => {
          this.ShowToastErrors(error);
          this.showSpinner.next(false);
          this.cd.detectChanges();
        },
      });
  }


  printPDF() {
    window.open(this.pdfSrc.value, '_blank');
  }

  downloadPDF() {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this.pdfSrc.value);
    link.setAttribute('download', `invoice.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  SendPDF(channel:string)
  {
    switch (this.pdfData.type) {
      case PdfType.Invoice:
        {
          this.SendInvoicePDF(channel);
        }
        break;
      case PdfType.Payment:
        {
          this.SendPaymentPdf(channel);
        }
        break;
      case PdfType.SalesReturn:
        {
          this.SendSalesReturnPdf(channel);
        }
        break;
      case PdfType.Refund:
      {
        this.SendRefundPdf(channel);
      }
      break;
    }
  }

  SendInvoicePDF(channel:string) {
    let channels:any[]=[];
    channels.push(channel);
    this.invoiceService.SendInvoicePDF(this.pdfData.clientId, this.pdfData.primaryId,channels).subscribe({
      next: (data: any) => {
        this.translate
          .get('TRANSACTION.MSG.INVOICE_MAIL_MSG')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
      },
      error: (error: AppErrors) => {
        this.toastMessageService.showError(error);
      },
    });
  }
  
  
  SendPaymentPdf(channel:string) {
    let channels:any[]=[];
    channels.push(channel);
    this.paymentService.SendPaymentPDF(this.pdfData.clientId, this.pdfData.primaryId,channels).subscribe({
      next: (data: any) => {
        this.translate
          .get('TRANSACTION.MSG.PAYMENT_MAIL_MSG')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
      },
      error: (error: AppErrors) => {
        this.toastMessageService.showError(error);
      },
    });
  }

  SendSalesReturnPdf(channel:string) {
    let channels:any[]=[];
    channels.push(channel);
      this.salesReturnService.SendSalesReturnPdf(this.pdfData.clientId, this.pdfData.secondaryId,this.pdfData.primaryId,channels)
        .subscribe({
          next: (data: any) => {
            this.pdfSrc.next(data);
            this.translate.get('TRANSACTION.MSG.SALES_RETURN_MAIL_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation)
            });
          },
          error: (error: AppErrors) => {
            this.toastMessageService.showError(error);
          },
        });
  }

  SendRefundPdf(channel:string) {
    let channels:any[]=[];
    channels.push(channel);
      this.refundService.SendRefundPdf(this.pdfData.clientId,this.pdfData.primaryId,channels)
        .subscribe({
          next: (data: any) => {
            this.pdfSrc.next(data);
            this.translate.get('TRANSACTION.MSG.INVOICE_MAIL_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation)
            });
          },
          error: (error: AppErrors) => {
            this.toastMessageService.showError(error);
          },
        });
  }

  voidData(form: UntypedFormGroup)
  {
    this.btnLoading= true;
    if (form.valid) {
      let voidRequest: any = {};
      voidRequest.void_reason = form.value.void_reason;

      let service:any;
      if(this.pdfData.type == PdfType.Invoice) service =  this.invoiceService.voidInvoice(this.pdfData.tenantId, this.pdfData.clientId, this.pdfData.primaryId, voidRequest);
      if(this.pdfData.type == PdfType.Payment) service =  this.paymentService.voidPayment(this.pdfData.tenantId, this.pdfData.clientId, this.pdfData.primaryId, voidRequest);
      if(this.pdfData.type == PdfType.SalesReturn) service =  this.salesReturnService.VoidSalesReturn(this.pdfData.tenantId, this.pdfData.clientId, this.pdfData.primaryId, voidRequest);
        service.subscribe({
          next: (data: any) => {
            this.translate.get(['SALES_RETURN.MSG.SALES_RETURN_VOID_MSG','TRANSACTION.MSG.PAYMENT_VOID_MSG','TRANSACTION.MSG.INVOICE_VOID_MSG'])
              .subscribe((translations) => {
                this.toastMessageService.showSuccess(this.pdfData.type == PdfType.Invoice ? translations['TRANSACTION.MSG.INVOICE_VOID_MSG'] : this.pdfData.type == PdfType.Payment ? translations['TRANSACTION.MSG.PAYMENT_VOID_MSG'] : this.pdfData.type == PdfType.SalesReturn ? translations['SALES_RETURN.MSG.SALES_RETURN_VOID_MSG'] : '')
              });
            this.pdfData.status= "void";
            this.dataUpdated= true;
            this.voidForm.reset();
            this.btnLoading= false;
            this.showVoid = false;
            this.cd.detectChanges();
          },
          error: (error: AppErrors) => {
            this.dataUpdated= false;
            this.toastMessageService.showError(error);
            this.btnLoading= false;
            this.cd.detectChanges();
          },
        });
    } else {
      Object.keys(this.voidForm.controls).forEach((field) => {
        const control = this.voidForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.btnLoading= false;
    }
  }

  ShowToastErrors(error: any) {
    this.toastMessageService.showError(error);
  }

  closePopup(event?: Event)
  {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.showSpinner.next(false);
    if(this.dataUpdated)
      this.ref.close(this.pdfData);
    else this.ref.close(null);
  }

  closeVoidPopup(event?: Event)
  {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.showSpinner.next(false);
    this.voidForm.reset();
    this.btnLoading=false;
    this.showVoid= false;
    this.cd.detectChanges();
  }

  get VoidReasonFormControl(): UntypedFormControl {
    return this.voidForm.controls.void_reason as UntypedFormControl;
  }

  get getPdfType()
  {
    return PdfType;
  }

  get loader()
  {
    return this.showSpinner.getValue();
  }

}